import React, { useState, Fragment } from 'react';
import { EditIcon, TrashIcon, EyeIcon, MailIcon, SendIcon, ClipboardIcon, RefreshCwIcon } from 'lucide-react';
import { EmailTemplate } from './MessagesManagement';
import { StatusBadge } from '../../StatusBadge';
interface EmailTemplatesListProps {
  emailTemplates: EmailTemplate[];
  canEditEmailTemplate: (template: EmailTemplate) => boolean;
  onEdit: (template: EmailTemplate) => void;
  onDelete: (id: string) => void;
  onSendTest: (id: string, email: string) => void;
  locale: string;
}
export function EmailTemplatesList({
  emailTemplates,
  canEditEmailTemplate,
  onEdit,
  onDelete,
  onSendTest,
  locale
}: EmailTemplatesListProps) {
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState<string>('');
  const [showTestEmailInput, setShowTestEmailInput] = useState<string | null>(null);
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key).then(() => {
      console.log(`Copied ${key} to clipboard`);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };
  const toggleExpand = (id: string) => {
    if (expandedTemplate === id) {
      setExpandedTemplate(null);
    } else {
      setExpandedTemplate(id);
    }
  };
  const toggleTestEmailInput = (id: string) => {
    if (showTestEmailInput === id) {
      setShowTestEmailInput(null);
    } else {
      setShowTestEmailInput(id);
      setTestEmail('');
    }
  };
  const handleSendTest = (id: string) => {
    if (testEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
      onSendTest(id, testEmail);
      setShowTestEmailInput(null);
    } else {
      alert('Please enter a valid email address');
    }
  };
  if (emailTemplates.length === 0) {
    return <div className="text-center py-12" data-id="element-16269">
        <MailIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-16270" />
        <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-16271">
          No email templates found
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-16272">
          No email templates match your current filters. Try adjusting your
          search criteria.
        </p>
      </div>;
  }
  return <div className="overflow-x-auto" data-id="element-16273">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-16274">
        <thead data-id="element-16275">
          <tr data-id="element-16276">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16277">
              Module
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16278">
              Key & Subject
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16279">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16280">
              Usage
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16281">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-16282">
          {emailTemplates.map(template => <Fragment key={template.id} data-id="element-16283">
              <tr className={expandedTemplate === template.id ? 'bg-gray-50' : 'hover:bg-gray-50'} data-id="element-16284">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-16285">
                  <div className="flex items-center" data-id="element-16286">
                    <span className="flex-shrink-0" data-id="element-16287">
                      <MailIcon className="h-5 w-5 text-indigo-500" data-id="element-16288" />
                    </span>
                    <span className="ml-2 text-sm font-medium text-gray-900" data-id="element-16289">
                      {template.module}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-16290">
                  <div data-id="element-16291">
                    <div className="flex items-center text-sm font-medium text-gray-900" data-id="element-16292">
                      <button onClick={() => handleCopyKey(template.key)} className="text-gray-500 hover:text-gray-700 mr-1" title="Copy key" data-id="element-16293">
                        <ClipboardIcon className="h-4 w-4" data-id="element-16294" />
                      </button>
                      <span className="font-mono" data-id="element-16295">{template.key}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500 line-clamp-2" data-id="element-16296">
                      {template.subject}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-16297">
                  <div className="flex flex-col space-y-1" data-id="element-16298">
                    <StatusBadge status={template.isActive ? 'success' : 'error'} text={template.isActive ? 'Active' : 'Inactive'} data-id="element-16299" />
                    {template.isSystemLocked && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-16300">
                        System Locked
                      </span>}
                    {template.editableByRoles.length > 0 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-16301">
                        RBAC Limited
                      </span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16302">
                  <div data-id="element-16303">Count: {template.usageCount}</div>
                  <div data-id="element-16304">Last: {formatDate(template.lastUsedAt)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-16305">
                  <div className="flex justify-end space-x-2" data-id="element-16306">
                    <button onClick={() => toggleExpand(template.id)} className="text-indigo-600 hover:text-indigo-900" title="View details" data-id="element-16307">
                      <EyeIcon className="h-5 w-5" data-id="element-16308" />
                    </button>
                    <button onClick={() => toggleTestEmailInput(template.id)} className="text-indigo-600 hover:text-indigo-900" title="Send test email" data-id="element-16309">
                      <SendIcon className="h-5 w-5" data-id="element-16310" />
                    </button>
                    {canEditEmailTemplate(template) && <>
                        <button onClick={() => onEdit(template)} className="text-indigo-600 hover:text-indigo-900" title="Edit template" data-id="element-16311">
                          <EditIcon className="h-5 w-5" data-id="element-16312" />
                        </button>
                        {!template.isSystemLocked && <button onClick={() => onDelete(template.id)} className="text-red-600 hover:text-red-900" title="Delete template" data-id="element-16313">
                            <TrashIcon className="h-5 w-5" data-id="element-16314" />
                          </button>}
                      </>}
                  </div>
                </td>
              </tr>
              {/* Test email input row */}
              {showTestEmailInput === template.id && <tr className="bg-blue-50" data-id="element-16315">
                  <td colSpan={5} className="px-6 py-4" data-id="element-16316">
                    <div className="flex items-center space-x-3" data-id="element-16317">
                      <label htmlFor="test-email" className="text-sm font-medium text-gray-700" data-id="element-16318">
                        Send test email to:
                      </label>
                      <input type="email" id="test-email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="email@example.com" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-64 sm:text-sm border-gray-300 rounded-md" data-id="element-16319" />
                      <button onClick={() => handleSendTest(template.id)} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-16320">
                        <SendIcon className="h-4 w-4 mr-2" data-id="element-16321" />
                        Send
                      </button>
                      <button onClick={() => setShowTestEmailInput(null)} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-16322">
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>}
              {/* Expanded template details */}
              {expandedTemplate === template.id && <tr className="bg-gray-50" data-id="element-16323">
                  <td colSpan={5} className="px-6 py-4" data-id="element-16324">
                    <div className="space-y-4" data-id="element-16325">
                      <div data-id="element-16326">
                        <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-16327">
                          Subject
                        </h4>
                        <div className="p-3 rounded-md text-sm bg-indigo-50 text-indigo-700" data-id="element-16328">
                          {template.subject}
                        </div>
                      </div>
                      <div data-id="element-16329">
                        <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-16330">
                          Available Merge Fields
                        </h4>
                        <div className="flex flex-wrap gap-2" data-id="element-16331">
                          {template.mergeFields.map(field => <span key={field} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-16332">
                              {`{{${field}}}`}
                            </span>)}
                        </div>
                      </div>
                      <div data-id="element-16333">
                        <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-16334">
                          Email Preview
                        </h4>
                        <div className="border border-gray-200 rounded-md p-4 bg-white" data-id="element-16335">
                          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                      __html: template.bodyHtml
                    }} data-id="element-16336" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-16337">
                        <div data-id="element-16338">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-16339">
                            Created
                          </h4>
                          <div className="text-sm text-gray-500" data-id="element-16340">
                            <div data-id="element-16341">{formatDate(template.createdAt)}</div>
                            <div data-id="element-16342">By: {template.createdBy}</div>
                          </div>
                        </div>
                        {template.modifiedAt && <div data-id="element-16343">
                            <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-16344">
                              Last Modified
                            </h4>
                            <div className="text-sm text-gray-500" data-id="element-16345">
                              <div data-id="element-16346">{formatDate(template.modifiedAt)}</div>
                              <div data-id="element-16347">By: {template.modifiedBy}</div>
                            </div>
                          </div>}
                        <div data-id="element-16348">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-16349">
                            RBAC Permissions
                          </h4>
                          {template.editableByRoles.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-16350">
                              {template.editableByRoles.map(role => <span key={role} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-16351">
                                  {role}
                                </span>)}
                            </div> : <div className="text-sm text-gray-500" data-id="element-16352">
                              {template.isSystemLocked ? 'System locked - no editing allowed' : 'No RBAC restrictions'}
                            </div>}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>}
            </Fragment>)}
        </tbody>
      </table>
    </div>;
}